import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Row, Col, Tooltip, Select, SelectProps, Button, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import HTTPService from '../../../../utils/makerequest';
import { IAuthentication } from '../../../../interfaces/authentication';
import { StateLoader } from '../../../../redux/StateLoader';
import { DeleteOutlined, TableOutlined } from '@ant-design/icons';
import AltisDataGrid, { IAltisDataGridColumns } from '../../../../AltisComponent/AltisDataGrid';

interface LaborTaskProps {
    formItemStyle?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
    selectedActivityId?: number; 
}

interface ILaborTaskRequest {
    activityId: number;
    laborTaskName: string;
}

interface ILaborTasks {
    laborTaskId?: number;
    laborTaskName: string;
}

interface ILaborTaskList {
    activityId: number;
    laborTasks: ILaborTasks[];
}

interface IActivity {
    activityId: number;
    activityName: string;
}

interface ITableData {
    id: number;
    laborTaskId?: number;  
    activityId: number;
    activityName: string;
    laborTaskName: string;
}

const LaborTask: React.FC<LaborTaskProps> = ({
    formItemStyle,
    inputStyle,
    selectedActivityId
}) => {
    const { t } = useTranslation();
    const [activityData, setActivityData] = useState<IActivity[]>([]);
    const [tableData, setTableData] = useState<ITableData[]>([]);
    const loader = new StateLoader();
    const userInfo: IAuthentication = loader.loadAuthState();
    const toast = useRef<Toast>(null);
    const [form] = Form.useForm();

    const defaultFormItemStyle = {
        marginBottom: '16px',
        width: '100%',
        ...formItemStyle
    };

    const defaultInputStyle = {
        width: '100%',
        borderRadius: '5px',
        ...inputStyle
    };

    const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
        return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
    };

    const handleDeleteRow = (id: number) => {
        setTableData(prev => prev.filter(item => item.id !== id));
        toast.current?.show({
            severity: 'success',
            summary: t('success'),
            detail: t('delete_success'),
            life: 3000
        });
    };

    useEffect(() => {
        if (userInfo?.companyId) {
            fetchActivity(userInfo.companyId);
        }

        if (selectedActivityId) {
            getActivityId(selectedActivityId);
        }
    }, []);

    const fetchActivity = async (companyId: number) => {
        try {
            const result = await HTTPService.GETActivityNames(companyId);
            setActivityData(result);
        } catch (error) {
            console.error(error);
            toast.current?.show({
                severity: 'error',
                summary: t('errors'),
                detail: t('error_message'),
                life: 3000
            });
        }
    };

    const getActivityId = (activityId: number) => {
        HTTPService.GET(`/Activity/ById?activityId=${activityId}`)
            .then((res) => {
                if (res.status === 200 && res.data) {
                    
                    const formattedData = {
                        activityId: res.data.activityId,
                        activityName: res.data.activityName,
                        laborTasks: res.data.laborTaskTBL || [] 
                    };

                    const tableRows = formattedData.laborTasks.map((task) => ({
                        id: task.laborTaskId || Date.now(),
                        laborTaskId: task.laborTaskId,  
                        activityId: task.activityId,
                        activityName: formattedData.activityName,
                        laborTaskName: task.laborTaskName
                    }));

                    setActivityData([{
                        activityId: formattedData.activityId,
                        activityName: formattedData.activityName
                    }]);
                    setTableData(tableRows);

                } else {
                    toast.current?.show({
                        severity: 'error',
                        summary: t('errors'),
                        detail: res.error?.message || t('error_fetching_data'),
                        life: 3000
                    });
                }
            })
            .catch((error) => {
                toast.current?.show({
                    severity: 'error',
                    summary: t('errors'),
                    detail: t('error_fetching_data'),
                    life: 3000
                });
                console.error(error);
            });
    };

    const handleAddRow = (values: any) => {
        const activity = activityData.find(a => a.activityId === selectedActivityId);

        setTableData(prev => [
            ...prev,
            {
                id: prev.length + 1,
                activityId: selectedActivityId,
                activityName: activity?.activityName || t('unknown_activity'),
                laborTaskName: values.laborTaskName
            }
        ]);
        form.resetFields();
    };

    const labortaskregister = async () => {
        try {
            if (tableData.length === 0) {
                toast.current?.show({
                    severity: 'warn',
                    summary: t('Warning'),
                    detail: t('no_data_to_save'),
                    life: 3000
                });
                return;
            }

            const laborTask = {
                activityId: selectedActivityId,
                laborTasks: tableData.map(row => ({
                    laborTaskId: row.laborTaskId,
                    laborTaskName: row.laborTaskName
                }))
            };

            const response = await HTTPService.POST('/Maintenance/registerLaborTask', laborTask);

            if (response.status === 200) {
                toast.current?.show({
                    severity: 'success',
                    summary: t('success'),
                    detail: t('labor_task_saved'),
                    life: 3000
                });

                setTableData([]);
                form.resetFields();
                window.location.replace('admin/care-management');
            } else {
                throw new Error(response.data);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: t('errors'),
                detail: t('labor_task_save_failed'),
                life: 3000
            });
        }
    };

    const columns: IAltisDataGridColumns[] = [
        {
            title: t('activity_name'),
            key: 'activityName',
            placeHolder: t('activity_name'),
            inputType: "input",
        },
        {
            title: t('laborTaskName'),
            key: 'laborTaskName',
            placeHolder: t('laborTaskName'),
            inputType: "input",
        },
        {
            title: t('Operations'),
            key: 'operations',
            placeHolder: t('Operations'),
            inputType: "operations",
            render: (_, record) => (
                <Popconfirm
                    title={t('delete_confirm')}
                    onConfirm={() => handleDeleteRow(record.id)}
                    okText={t('yes')}
                    cancelText={t('no')}
                >
                    <Button
                        size='small'
                        icon={<DeleteOutlined />}
                        onClick={(e) => { e.stopPropagation(); }}
                        style={{ width: '60px', borderRadius: '70px' }}
                    />
                </Popconfirm >
            )
        },
    ];

    return (
        <>
            <Toast ref={toast} />
            <Row>
                <Col span={24}>
                    <Form
                        form={form}
                        onFinish={handleAddRow}
                    >
                        <Form.Item
                            label={t('laborTaskName')}
                            name="laborTaskName"
                            rules={[{ required: true, message: t('laborTaskName') }]}
                            style={defaultFormItemStyle}
                        >
                            <Input
                                placeholder={t('laborTaskName')}
                                style={defaultInputStyle}
                            />
                        </Form.Item>

                        <Button
                            type="primary"
                            htmlType="submit"
                            style={{
                                borderRadius: '5px',
                                marginBottom: '30px'
                            }}
                        >
                            <TableOutlined style={{ fontSize: '18px' }} />
                            <span>{t('addTable')}</span>
                        </Button>
                    </Form>
                </Col>

                <AltisDataGrid
                    data={tableData}
                    columns={columns}
                    onChange={() => { }}
                    total={tableData.length}
                />
            </Row>

            <Form layout="vertical">
                <Form.Item style={{ marginTop: '15px' }}>
                    <Row gutter={[8, 8]} justify="end" align="middle">
                        <Col xs={12} sm={12} md={4}>
                            <Button
                                block
                                type="primary"
                                style={{ borderRadius: '5px' }}
                                onClick={labortaskregister}
                            >
                                {t('save')}
                            </Button>
                        </Col>
                    </Row>
                </Form.Item>
            </Form>
        </>
    );
};

export default LaborTask;
